import React from "react";
import { BasePage } from "@/components/Page/BasePage";
import { useStore } from "@/stores/useStore";
import {
  RouterProvider as BaseRouterProvider,
  Outlet,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";
import { Spinner } from "@/components/Common/Spinner";
import { GlobalConfigPage } from "@/pages/(superAdmin)/GlobalConfig.page";
import { ShopConfigPage } from "@/pages/(superAdmin)/(shop)/ShopConfig.page";
import DebugCardCreatePage from "@/pages/(superAdmin)/(debug)/CreateCard.page";
import { AuthState } from "@/types/v1";
import {
  ActivityFeedPage,
  AutomationsPage,
  CardConfigPage,
  CompanyInfoPage,
  DebugPage,
  EmployeesPage,
  ErrorPage,
  IntegrationsPage,
  IsAuthenticatedRedirect,
  LayoutBuilderPage,
  LoginPage,
  ProtectedRoute,
  ResetPasswordPage,
  ShopDashboardPage,
  ShopLayoutPage,
  TermsAndConditionsPage,
  VerifyPage,
} from "@/pages";
import { MessageBoardLayoutPage } from "@/pages/(shop)/messageBoard/_layout.page";
import { MessageBoardRootPage } from "@/pages/(shop)/messageBoard/MessageBoardRoot.page";
import { MessageBoardPage } from "@/pages/(shop)/messageBoard/MessageBoard.page";
import { LayoutContainersPage } from "@/pages/(shop)/dashboard/LayoutContainers.page";
import { LayoutsPage } from "@/pages/(shop)/dashboard/Layouts.page";
import { ContainersPage } from "@/pages/(shop)/dashboard/Containers.page";
import { GoListPage } from "@/pages/GoListPage";
import MetricsPage from "@/pages/(shop)/dashboard/Metrics.page";
import ContainerMetricsPage from "@/pages/(shop)/dashboard/ContainerMetrics.page";
import { AuthActionPage } from "@/pages/(auth)/AuthAction.page";
import { RegisterPage } from "@/pages/(auth)/Register.page";
import { CreateShopPage } from "@/pages/(subscription)/CreateShop.page";
import { SubscriptionReturnPage } from "@/pages/(subscription)/SubscriptionReturn.page";
import { AppConfigPage } from "@/pages/(superAdmin)/AppConfig.page";
import { sentry } from "@/services/sentry";
import UsersPage from "@/pages/(superAdmin)/Users.page";
import ShopsPage from "@/pages/(superAdmin)/Shops.page";
import ShopPage from "@/pages/(superAdmin)/(shop)/Shop.page";
import ShopConfigAuditPage from "@/pages/(superAdmin)/ShopConfigAudit.page";
import CustomerUpdatesPage from "@/pages/(shop)/dashboard/CustomerUpdates.page";
import CustomerUpdatePage from "@/pages/(shop)/dashboard/CustomerUpdate.page";
import CardDetailsPage from "@/pages/(shop)/cards/CardDetails.page";
import CardsListPage from "@/pages/(shop)/cards/CardsList.page";
import DashboardListPage from "@/pages/(shop)/dashboard-list";

const sentryCreateBrowserRouter =
  sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const router = ({ authState }: { authState: AuthState }) =>
  sentryCreateBrowserRouter(
    createRoutesFromElements(
      <Route
        path="/"
        errorElement={
          <BasePage>
            <React.Suspense fallback={<Spinner />}>
              <ErrorPage />
            </React.Suspense>
          </BasePage>
        }
        element={
          <BasePage>
            <React.Suspense fallback={<Spinner />}>
              <Outlet />
            </React.Suspense>
          </BasePage>
        }
      >
        <Route
          path=""
          element={
            <ProtectedRoute>
              <ShopLayoutPage />
            </ProtectedRoute>
          }
        />
        <Route path="/auth-actions" element={<AuthActionPage />} />
        <Route path="/verify" element={<VerifyPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/resetpassword" element={<ResetPasswordPage />} />
        <Route path="/terms" element={<TermsAndConditionsPage />} />
        <Route path="/create-shop" element={<CreateShopPage />} />
        <Route path="/users" element={<Outlet />}>
          <Route path="" element={<UsersPage />} />
        </Route>
        <Route path="/shops" element={<Outlet />}>
          <Route path="" element={<ShopsPage />} />
          <Route path=":shopId" element={<ShopPage />} />
        </Route>
        <Route path="/shop-config-audit" element={<ShopConfigAuditPage />} />
        <Route
          path="/onboarding/complete"
          element={<SubscriptionReturnPage />}
        />

        <Route
          path="/:shopId"
          element={
            <ProtectedRoute>
              <ShopLayoutPage />
            </ProtectedRoute>
          }
        >
          <Route path="" element={<ShopDashboardPage />} />
          <Route path="dashboard-list" element={<DashboardListPage />} />
          <Route path="layoutBuilder" element={<LayoutBuilderPage />} />
          <Route path="cardConfig" element={<CardConfigPage />} />
          <Route path="containers" element={<Outlet />}>
            <Route path="" element={<ContainersPage />} />
          </Route>
          <Route path="layouts" element={<LayoutsPage />} />
          <Route
            path="layoutBuilder/containers"
            element={<LayoutContainersPage />}
          />
          <Route path="settings" element={<Outlet />}>
            <Route path="shop" element={<CompanyInfoPage />} />
          </Route>
          <Route path="automations" element={<AutomationsPage />} />
          <Route path="employees" element={<EmployeesPage />} />
          <Route path="integrations" element={<IntegrationsPage />} />
          <Route path="companyInfo" element={<CompanyInfoPage />} />
          <Route path="activityFeed" element={<ActivityFeedPage />} />
          <Route path="metrics" element={<MetricsPage />} />
          <Route path="containerMetrics" element={<ContainerMetricsPage />} />
          <Route path="messageBoard" element={<MessageBoardLayoutPage />}>
            <Route path="" element={<MessageBoardRootPage />} />
            <Route path=":conversationId" element={<MessageBoardPage />} />
          </Route>
          <Route path="customer-updates" element={<Outlet />}>
            <Route index element={<CustomerUpdatesPage />} />
            <Route path=":customerUpdateId" element={<CustomerUpdatePage />} />
          </Route>
          <Route path="cards" element={<Outlet />}>
            <Route index element={<CardsListPage />} />
            <Route path=":cardId" element={<CardDetailsPage />} />
          </Route>
          <Route path="features" element={<ShopConfigPage />} />
          <Route path="go-list" element={<GoListPage />} />
        </Route>

        {authState === "authenticated" && (
          <>
            <Route path="/debug" element={<Outlet />}>
              <Route path="" element={<DebugPage />} />
              <Route path="cards" element={<Outlet />}>
                <Route path="create" element={<DebugCardCreatePage />} />
              </Route>
            </Route>
            <Route path="config" element={<Outlet />}>
              <Route path="app" element={<AppConfigPage />} />
            </Route>
            <Route path="features" element={<Outlet />}>
              <Route path="global" element={<GlobalConfigPage />} />
            </Route>
          </>
        )}
        <Route path="*" element={<IsAuthenticatedRedirect />} />
      </Route>
    )
  );
export const RouterProvider = () => {
  const authState = useStore((s) => s.authState);

  return (
    <BaseRouterProvider
      router={router({
        authState,
      })}
    />
  );
};
